import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Event as EventIcon, Place as PlaceIcon, Phone as PhoneIcon } from '@mui/icons-material';
import PageLayout from '../../components/layout/PageLayout';
import PageHeader from '../../components/layout/PageHeader';
import { useMyBookings } from '../../hooks/account/useMyBookings';
import { useToast } from '../../context/ToastContext';
import { BOOKING_STATUS_COLOR, formatSlot } from '../../constants/scheduling';
import { eventDayParts } from '../../constants/community';
import { BookingCardSkeleton, Loading, StackSkeleton } from '../../components/layout/Skeletons';

/**
 * A resident's appointments.
 *
 * The status is the first thing on the card, because "have they answered yet"
 * is the only question anyone opens this page to ask.
 */
function MyBookings() {
  const toast = useToast();
  const { bookings, loading, error, act } = useMyBookings('/me/bookings');
  const [cancelling, setCancelling] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const confirmCancel = async () => {
    setBusy(true);
    const message = await act(cancelling.uuid, 'cancel', { reason: reason.trim() || null });
    setBusy(false);

    if (message) {
      toast.error(message);
      return;
    }

    toast.success('Rendez-vous annulé.');
    setCancelling(null);
    setReason('');
  };

  return (
    <PageLayout centered={false}>
      <Box sx={{ width: '100%' }}>
        <PageHeader
          title="Mes rendez-vous"
          lede="Une demande devient un rendez-vous quand le professionnel la confirme. Vous recevez un courriel dans les deux cas."
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Loading label="Chargement de vos rendez-vous">
            <StackSkeleton count={2}>{BookingCardSkeleton}</StackSkeleton>
          </Loading>
        ) : (
          <Stack spacing={2.25}>
            {0 === bookings.length && (
              <Paper variant="outlined" sx={{ p: 5, textAlign: 'center', borderStyle: 'dashed' }}>
                <Typography variant="h4" component="p" sx={{ mb: 1 }}>
                  Vous n’avez aucun rendez-vous.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Les professionnels de l’annuaire qui acceptent les rendez-vous affichent leurs
                  créneaux sur leur fiche.
                </Typography>
                <Button component={RouterLink} to="/services" variant="contained" size="large">
                  Trouver un professionnel
                </Button>
              </Paper>
            )}

            {bookings.map((booking) => {
              const day = booking.preferred_at ? eventDayParts(booking.preferred_at) : null;
              const waiting = 'REQUESTED' === booking.status;

              return (
              <Paper
                key={booking.uuid}
                variant="outlined"
                sx={{ p: { xs: 2.5, md: 3 }, display: 'flex', gap: 2.5 }}
              >
                {day && (
                  <Box
                    aria-hidden="true"
                    sx={{
                      width: 64,
                      flexShrink: 0,
                      textAlign: 'center',
                      py: 1.25,
                      borderRadius: '14px',
                      display: { xs: 'none', sm: 'block' },
                      bgcolor: waiting ? 'warning.light' : 'success.light',
                    }}
                  >
                    <Typography
                      variant="h4"
                      component="span"
                      sx={{ display: 'block', lineHeight: 1, color: waiting ? 'primary.main' : 'secondary.main' }}
                    >
                      {day.day}
                    </Typography>
                    <Typography
                      variant="overline"
                      component="span"
                      sx={{ display: 'block', fontSize: '0.78125rem', mt: 0.4 }}
                    >
                      {day.month}
                    </Typography>
                  </Box>
                )}

                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.25, flexWrap: 'wrap' }}>
                    <Chip
                      size="small"
                      label={booking.status_label}
                      color={BOOKING_STATUS_COLOR[booking.status] || 'default'}
                    />
                  </Box>

                  <Typography variant="h4" component="h2">
                    {booking.subject}
                  </Typography>

                  <Typography
                    component={RouterLink}
                    to={`/prestataires/${booking.provider.slug}`}
                    variant="body1"
                    sx={{ display: 'inline-block', mt: 0.5, color: 'primary.main', fontWeight: 600 }}
                  >
                    {booking.provider.name}
                  </Typography>

                  <Stack spacing={0.75} sx={{ mt: 1.75 }}>
                    {/* A request may carry no moment at all: "quand vous
                        voulez" is an answer the form accepts. */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <EventIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {booking.preferred_at
                          ? formatSlot(booking.preferred_at)
                          : 'Aucun moment précis demandé'}
                      </Typography>
                    </Box>

                    {booking.location && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        <PlaceIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                        <Typography variant="body1">{booking.location}</Typography>
                      </Box>
                    )}

                    {/* Handed over only once there is something to turn up to. */}
                    {booking.provider.phone && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        <PhoneIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                        <Typography variant="body1">
                          <Link href={`tel:${booking.provider.phone}`} sx={{ fontWeight: 700 }}>
                            {booking.provider.phone}
                          </Link>
                        </Typography>
                      </Box>
                    )}
                  </Stack>

                  {booking.decision_reason && (
                    <Alert severity={'DECLINED' === booking.status ? 'warning' : 'info'} sx={{ mt: 2 }}>
                      {booking.decision_reason}
                    </Alert>
                  )}

                  {booking.open && (
                    <Button
                      variant="outlined"
                      color="error"
                      sx={{ mt: 2.5 }}
                      onClick={() => setCancelling(booking)}
                    >
                      Annuler ce rendez-vous
                    </Button>
                  )}
                </Box>
              </Paper>
              );
            })}
          </Stack>
        )}
      </Box>

      <Dialog open={Boolean(cancelling)} onClose={() => setCancelling(null)} fullWidth maxWidth="sm">
        <DialogTitle>Annuler ce rendez-vous</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Le créneau repart aussitôt et le professionnel est prévenu. Un mot d’explication
            est apprécié, mais pas obligatoire.
          </DialogContentText>
          <TextField
            label="Motif (facultatif)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            inputProps={{ maxLength: 500 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelling(null)} disabled={busy} variant="outlined">
            Revenir
          </Button>
          <Button color="error" variant="contained" onClick={confirmCancel} disabled={busy}>
            Annuler le rendez-vous
          </Button>
        </DialogActions>
      </Dialog>
    </PageLayout>
  );
}

export default MyBookings;
